import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { moduleService } from "../../services/moduleService";
import { Spinner } from "../ui/Spinner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";

interface BasicInfoProps {
  moduleId?: string;
  onSave: (data: ModuleBasicInfo) => void;
}

interface ModuleBasicInfo {
  id?: number;
  name: string;
  description: string;
  sequenceNumber: number;
  sellerType: string;
  pointsAwarded: number;
  startDate: string;
  endDate: string;
}

export function BasicInfo({ moduleId, onSave }: BasicInfoProps) {
  const { toast } = useToast();

  const [info, setInfo] = React.useState<ModuleBasicInfo>({
    id: moduleId ? parseInt(moduleId) : 0,
    name: "",
    description: "",
    sequenceNumber: 0,
    sellerType: "",
    pointsAwarded: 0,
    startDate: "",
    endDate: "",
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSequenceLoading, setIsSequenceLoading] = React.useState(false);
  const isEditMode = Boolean(moduleId);

  // Use the same input style as the Input component
  const inputClassName = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

  React.useEffect(() => {
    if (moduleId) {
      const fetchInfo = async () => {
        try {
          setIsLoading(true);
          const response = await moduleService.getModuleById(moduleId);
          if (response) {
            setInfo({
              id: response.id,
              name: response.name,
              description: response.description || "",
              sequenceNumber: response.sequenceNumber,
              sellerType: response.sellerType,
              pointsAwarded: response.pointsAwarded,
              startDate: response.startDate ? response.startDate.split('T')[0] : "",
              endDate: response.endDate ? response.endDate.split('T')[0] : "",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to fetch module information",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      };

      fetchInfo();
    }
  }, [moduleId, toast]);

  // Fetch max sequence number if creating new module
  React.useEffect(() => {
    if (!isEditMode) {
      const fetchMaxSequence = async () => {
        setIsSequenceLoading(true);
        try {
          const modules = await moduleService.getAllModules();
          const maxSeq = modules.reduce((max, m) =>
            m.sequenceNumber > max ? m.sequenceNumber : max, 0);
          setInfo((prev) => ({ ...prev, sequenceNumber: maxSeq + 1 }));
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to fetch modules for sequence number",
            variant: "destructive",
          });
        } finally {
          setIsSequenceLoading(false);
        }
      };
      fetchMaxSequence();
    }
  }, [isEditMode, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info.name.trim()) {
      toast({
        title: "Error",
        description: "Name cannot be empty",
        variant: "destructive",
      });
      return;
    }
    try {
      setIsLoading(true);
      const savedModule = await moduleService.saveModule(info);
      setInfo(prev => ({
        ...prev,
        id: savedModule.id
      }));
      onSave({
        ...info,
        id: savedModule.id
      });
      toast({
        title: "Success",
        description: "Module information saved successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save module information",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <Spinner label="Loading module information..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Title</Label>
            <Input
              id="name"
              value={info.name}
              onChange={(e) => setInfo({ ...info, name: e.target.value })}
              placeholder="Enter module title"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={info.description}
              onChange={(e) => setInfo({ ...info, description: e.target.value })}
              placeholder="Enter module description"
              className="min-h-[100px]"
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sequenceNumber">Sequence Number</Label>
              <Input
                id="sequenceNumber"
                type="number"
                value={info.sequenceNumber}
                onChange={(e) =>
                  setInfo({ ...info, sequenceNumber: parseInt(e.target.value) })
                }
                min="0"
                disabled={true}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sellerType">Seller Type</Label>
              <Select
                value={info.sellerType}
                onValueChange={(value: string) => setInfo({ ...info, sellerType: value })}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select seller type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="zonal">Zonal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pointsAwarded">Points</Label>
              <Input
                id="pointsAwarded"
                type="number"
                value={info.pointsAwarded}
                onChange={(e) =>
                  setInfo({ ...info, pointsAwarded: parseInt(e.target.value) })
                }
                min="0"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <DatePicker
                id="startDate"
                selected={info.startDate ? parseISO(info.startDate) : null}
                onChange={(date: Date | null) =>
                  setInfo({ ...info, startDate: date ? format(date, "yyyy-MM-dd") : "" })
                }
                dateFormat="yyyy-MM-dd"
                className={inputClassName}
                placeholderText="Select start date"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <DatePicker
                id="endDate"
                selected={info.endDate ? parseISO(info.endDate) : null}
                onChange={(date: Date | null) =>
                  setInfo({ ...info, endDate: date ? format(date, "yyyy-MM-dd") : "" })
                }
                dateFormat="yyyy-MM-dd"
                className={inputClassName}
                placeholderText="Select end date"
                disabled={isLoading}
                minDate={info.startDate ? parseISO(info.startDate) : undefined}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Information"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
} 